import { Screen, ScreenConfig } from '../core/Screen';
import { APIManager, StateManager, ScreenRouter } from '../../managers';
import { getPortraitUrl, getPortraitTuning } from '../../combat/spriteConfig';
import { rarityColor } from '../components/TooltipContent';
import { screenHeaderHTML, renderScreenCurrencies } from '../components/ScreenHeader';
import { elementColor } from '../../utils/elementColors';
import { formatUnitClass, formatElement } from '../components/GameTerms';
import type { UnitData, CrawlDetails, CrawlData, ConfigData, PartyUnit } from '../../types';
import { SKILL_SLOT_KEYS } from '../../types';

type CrawlType = 'daily' | 'event' | 'free' | 'campaign' | 'campaign_replay';

export class HeroSelectScreen extends Screen {
  readonly screenConfig: ScreenConfig = {
    id: 'hero-select',
    name: 'Select Strand',
    persistent: true,
    needsPhaser: false,
  };

  private selectedSlots: (string | null)[] = [null, null, null];
  private crawlId: string | null = null;
  private crawlType: CrawlType = 'daily';
  private crawlDetails: CrawlDetails | null = null;
  private starting = false;

  constructor() {
    super({ rootId: 'hero-select-root' });
  }

  createDOM(container: HTMLElement): void {
    const root = document.createElement('div');
    root.id = 'hero-select-root';
    root.innerHTML = `
      <div class="hero-select-screen">
        ${screenHeaderHTML('Select Strand')}
        <div class="hs-body">
          <div class="hs-party-strip"></div>
          <div class="hs-element-brief"></div>
          <div class="hs-gallery"></div>
        </div>
        <div class="hs-action">
          <button class="hs-back-btn" type="button">Back</button>
          <button class="hs-begin-btn" type="button" disabled>Begin Crawl</button>
        </div>
      </div>
    `;
    container.appendChild(root);
  }

  protected cacheElements(): void {}
  protected bindEvents(): void {}
  protected setupSubscriptions(): void {}

  onEnter(params?: Record<string, unknown>): void {
    const hasNewCrawl = params?.crawlId != null;
    if (!hasNewCrawl && this.crawlId) {
      this.starting = false;
      this.onRender();
      return;
    }

    this.crawlId = (params?.crawlId as string) ?? this.crawlId;
    this.crawlType = (params?.crawlType as CrawlType) ?? this.crawlType;
    this.crawlDetails = (params?.crawlDetails as CrawlDetails) ?? this.crawlDetails;
    this.starting = false;

    if (!this.crawlId || (!this.crawlDetails && this.crawlType !== 'free')) {
      ScreenRouter.back();
      return;
    }

    const unitData = StateManager.get('unitData') as UnitData | undefined;
    const party = unitData?.party ?? [];
    this.selectedSlots = [
      party[0]?.id ?? null,
      party[1]?.id ?? null,
      party[2]?.id ?? null,
    ];

    this.onRender();
  }

  protected onRender(): void {
    if (this.rootEl) renderScreenCurrencies(this.rootEl, 'strand-select');
    this.render();
  }

  private render(): void {
    this.renderPartyStrip();
    this.renderElementBrief();
    this.renderGallery();
    this.updateButton();
    this.bindActionBar();
  }

  private getSelectedCount(): number {
    return this.selectedSlots.filter(Boolean).length;
  }

  // --- Party Strip (3 selected portrait slots) ---

  private renderPartyStrip(): void {
    const stripEl = this.$('.hs-party-strip');
    if (!stripEl) return;

    const unitData = StateManager.get('unitData') as UnitData | undefined;
    const configData = StateManager.get('configData') as ConfigData | undefined;
    const roster = unitData?.roster ?? [];

    const slotsHTML = this.selectedSlots.map((unitId, i) => {
      if (!unitId) {
        return `
          <div class="hs-party-slot hs-party-slot--empty" data-slot="${i}">
            <span class="hs-party-num">${i + 1}</span>
          </div>
        `;
      }

      const unit = roster.find(u => u.id === unitId);
      if (!unit) {
        return `<div class="hs-party-slot hs-party-slot--empty" data-slot="${i}"><span class="hs-party-num">${i + 1}</span></div>`;
      }

      const spriteKey = configData?.unitTemplates?.[unit.unitClass]?.spriteKey ?? 'warrior';
      const eColor = elementColor((unit.defensiveTypes ?? [])[0] ?? unit.element);
      const src = getPortraitUrl(spriteKey);
      const tuning = getPortraitTuning(spriteKey);

      const imgParts: string[] = [];
      if (tuning.scale != null) imgParts.push(`transform:scale(${tuning.scale})`);
      const hasX = tuning.offsetX != null;
      const hasY = tuning.offsetY != null;
      if (hasX || hasY) imgParts.push(`object-position:${hasX ? `${tuning.offsetX}%` : 'center'} ${hasY ? `${tuning.offsetY}%` : '0%'}`);
      const imgStyle = imgParts.length ? ` style="${imgParts.join(';')}"` : '';

      return `
        <div class="hs-party-slot hs-party-slot--filled" data-slot="${i}" style="--slot-element:${eColor}">
          <img class="hs-party-portrait" src="${src}" alt="${unit.name}" draggable="false"${imgStyle}>
          <span class="hs-party-num">${i + 1}</span>
          <span class="hs-party-deselect">&times;</span>
        </div>
      `;
    }).join('');

    const hint = this.getSelectedCount() < 3
      ? '<span class="hs-party-hint">Tap strands to fill your party</span>'
      : '';

    stripEl.innerHTML = `<div class="hs-party-slots">${slotsHTML}</div>${hint}`;

    stripEl.querySelectorAll('.hs-party-slot--filled').forEach(el => {
      el.addEventListener('click', () => {
        const slot = Number((el as HTMLElement).dataset.slot);
        this.selectedSlots[slot] = null;
        this.render();
      });
    });
  }

  // --- Element Briefing Bar ---

  private renderElementBrief(): void {
    const briefEl = this.$('.hs-element-brief');
    if (!briefEl) return;

    if (!this.crawlDetails || this.crawlType === 'campaign' || this.crawlType === 'campaign_replay' || this.crawlType === 'free') {
      briefEl.style.display = 'none';
      return;
    }

    const elements = new Set<string>();
    for (const enc of this.crawlDetails.encounters) {
      for (const enemy of enc.enemies) {
        if (enemy.defensiveTypes && enemy.defensiveTypes.length > 0) {
          for (const dt of enemy.defensiveTypes) elements.add(dt);
        } else if (enemy.element) {
          elements.add(enemy.element);
        }
      }
    }

    if (elements.size === 0) {
      briefEl.style.display = 'none';
      return;
    }

    briefEl.style.display = '';
    const tags = [...elements].map(el => {
      const eColor = elementColor(el);
      const label = formatElement(el);
      return `<span class="hs-element-tag" style="color:${eColor}">${label}</span>`;
    }).join('');

    briefEl.innerHTML = `<span class="hs-brief-label">Expect:</span>${tags}`;
  }

  // --- Gallery Grid (gacha-style portrait cards) ---

  private renderGallery(): void {
    const galleryEl = this.$('.hs-gallery');
    if (!galleryEl) return;

    const unitData = StateManager.get('unitData') as UnitData | undefined;
    const configData = StateManager.get('configData') as ConfigData | undefined;
    const roster = unitData?.roster ?? [];

    galleryEl.innerHTML = roster.map(unit => {
      const pUnit = unit as PartyUnit;
      const spriteKey = configData?.unitTemplates?.[unit.unitClass]?.spriteKey ?? 'warrior';
      const rColor = rarityColor(unit.rarity ?? 'normal');
      const eColor = elementColor((unit.defensiveTypes ?? [])[0] ?? unit.element);
      const className = unit.unitClass ? formatUnitClass(unit.unitClass) : '';
      const src = getPortraitUrl(spriteKey);
      const selectedIdx = this.selectedSlots.indexOf(unit.id);
      const isSelected = selectedIdx !== -1;
      const hasSkills = SKILL_SLOT_KEYS.some(key => pUnit[key]);

      return `
        <div class="hs-card${isSelected ? ' hs-card--selected' : ''}${!hasSkills ? ' hs-card--warn' : ''}" data-unit-id="${unit.id}" style="--card-element:${eColor}">
          ${isSelected ? `<span class="hs-card-badge">${selectedIdx + 1}</span>` : ''}
          ${!hasSkills && !isSelected ? '<span class="hs-card-warn-icon">!</span>' : ''}
          <img class="hs-card-portrait" src="${src}" alt="${unit.name}" draggable="false">
          <div class="hs-card-footer">
            <span class="hs-card-name" style="color:${rColor}">${unit.name}</span>
            <span class="hs-card-meta">Lv.${unit.level ?? 1} ${className}</span>
          </div>
        </div>
      `;
    }).join('');

    galleryEl.querySelectorAll('.hs-card').forEach(el => {
      el.addEventListener('click', () => {
        const unitId = (el as HTMLElement).dataset.unitId!;
        this.toggleHero(unitId);
      });
    });
  }

  private toggleHero(unitId: string): void {
    const existingIdx = this.selectedSlots.indexOf(unitId);
    if (existingIdx !== -1) {
      // Deselect
      this.selectedSlots[existingIdx] = null;
    } else {
      // Select into first empty slot
      const emptyIdx = this.selectedSlots.indexOf(null);
      if (emptyIdx === -1) return;
      this.selectedSlots[emptyIdx] = unitId;
    }
    this.render();
  }

  private updateButton(): void {
    const btn = this.$('.hs-begin-btn') as HTMLButtonElement | null;
    if (!btn) return;

    btn.textContent = this.starting ? 'Starting...' : 'Begin Crawl';

    let hint = '';
    if (this.getSelectedCount() === 3 && !this.starting) {
      const unitData = StateManager.get('unitData') as UnitData | undefined;
      const roster = unitData?.roster ?? [];
      const naked = this.selectedSlots
        .filter(Boolean)
        .map(id => roster.find(u => u.id === id) as PartyUnit | undefined)
        .find(u => u && SKILL_SLOT_KEYS.every(key => !u[key]));
      if (naked) hint = `${naked.name} has no skills equipped`;
    }

    btn.disabled = this.getSelectedCount() !== 3 || this.starting || !!hint;

    let hintEl = this.rootEl?.querySelector('.hs-action-hint') as HTMLElement | null;
    if (hint) {
      if (!hintEl) {
        hintEl = document.createElement('div');
        hintEl.className = 'hs-action-hint';
        btn.parentElement?.appendChild(hintEl);
      }
      hintEl.textContent = hint;
    } else if (hintEl) {
      hintEl.remove();
    }
  }

  private bindActionBar(): void {
    this.$('.hs-back-btn')?.addEventListener('click', () => ScreenRouter.back());
    this.$('.hs-begin-btn')?.addEventListener('click', () => this.beginCrawl());
  }

  private async beginCrawl(): Promise<void> {
    if (this.starting || this.getSelectedCount() !== 3) return;
    this.starting = true;
    this.updateButton();

    const unitIds = this.selectedSlots as string[];

    let result;
    if (this.crawlType === 'daily') {
      result = await APIManager.startCrawl(unitIds);
    } else if (this.crawlType === 'event') {
      result = await APIManager.startEventCrawl(unitIds);
    } else if (this.crawlType === 'campaign') {
      result = await APIManager.startCampaignRun(unitIds);
    } else if (this.crawlType === 'campaign_replay') {
      result = await APIManager.startCampaignReplay(this.crawlId!, unitIds);
    } else {
      result = await APIManager.startFreeCrawl(unitIds);
    }

    if (!result.success || !result.data) {
      console.error('[HeroSelect] Failed to start crawl:', result.error);
      this.starting = false;
      this.updateButton();
      return;
    }

    const { run, combatResult } = result.data;

    if (this.crawlType === 'event') {
      const eventCrawlData = StateManager.get('eventCrawlData') as CrawlData | undefined;
      if (eventCrawlData) {
        StateManager.set('eventCrawlData', { ...eventCrawlData, run });
        StateManager.set('crawlData', { ...eventCrawlData, run });
      }
    } else if (this.crawlType === 'free') {
      const freeConfig = {
        id: run.crawlConfigId,
        name: 'Free Crawl',
        encounters: run.encounters.map((eid: string) => ({ tier: 'standard' as const, encounterId: eid })),
        completionBonus: { xp: 50, gold: 12 },
        dropItemLevel: 6,
        bgKey: 'skarn_cave',
      };
      StateManager.set('crawlData', { config: freeConfig, run } as CrawlData);
    } else if (this.crawlDetails) {
      StateManager.set('crawlData', { config: this.crawlDetails.config, run } as CrawlData);
    }

    ScreenRouter.replace('combat', { combatResult, run });
  }
}
